import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import NeoButton from "../../../components/Common/NeoButton";
import NeoInput from "../../../components/Common/NeoInput";
import NeoBadge from "../../../components/Common/NeoBadge";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import NeoFinancialTable from "../../../components/Common/NeoFinancialTable";
import ConfirmPaymentModal from "./components/ConfirmPaymentModal";
import usePlatformBilling from "./hooks/usePlatformBilling";
import { BILLING_STATUS_LABELS, BILLING_STATUS_COLORS, STATUS_FILTER_OPTIONS } from "./constants";

const PlatformBilling = () => {
    const { items, loading, addManualPayment } = usePlatformBilling();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "all");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [formConfirm, setFormConfirm] = useState({
        method: "Pix",
        paidDate: new Date().toISOString().split("T")[0],
    });

    const handleOpenPaymentModal = (session) => {
        setSelectedSession(session);
        setModalOpen(true);
    };

    const handleConfirmPayment = async () => {
        if (!selectedSession) return;
        await addManualPayment(selectedSession.idTenant || selectedSession.id, {
            amount: selectedSession.amount,
            method: formConfirm.method,
            paidAt: formConfirm.paidDate,
            sessionId: selectedSession.id,
        });
        setModalOpen(false);
    };

    const filteredSessions = items.filter(s => {
        const q = search.toLowerCase();
        const matchesSearch = (s.tenantName || s.schoolName || "").toLowerCase().includes(q) || (s.id || "").includes(search);
        const matchesStatus = filterStatus === "all" || s.paymentStatus === filterStatus || s.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const totalPagas = items.filter(s => s.paymentStatus === "approved" || s.status === "pago").reduce((acc, s) => acc + (s.amount || 0), 0);
    const totalPendentes = items.filter(s => s.paymentStatus === "pending" || s.status === "pending").reduce((acc, s) => acc + (s.amount || 0), 0);
    const totalRejeitadas = items.filter(s => s.paymentStatus === "rejected" || s.status === "vencido").reduce((acc, s) => acc + (s.amount || 0), 0);

    const summaryData = [
        {
            label: "Total Recebido (Mês)",
            value: `R$ ${totalPagas.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
            count: items.filter(s => s.paymentStatus === "approved" || s.status === "pago").length,
            icon: "mdi-check-circle-outline",
            statusClass: "status-paid",
        },
        {
            label: "Total Pendente",
            value: `R$ ${totalPendentes.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
            count: items.filter(s => s.paymentStatus === "pending" || s.status === "pending").length,
            icon: "mdi-clock-outline",
            statusClass: "status-pending",
        },
        {
            label: "Total Vencido / Rejeitado",
            value: `R$ ${totalRejeitadas.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
            count: items.filter(s => s.paymentStatus === "rejected" || s.status === "vencido").length,
            icon: "mdi-alert-circle-outline",
            statusClass: "status-overdue",
        },
    ];

    const columns = [
        {
            key: "schoolName",
            label: "Fatura / Cliente",
            width: "25%",
            render: (row) => (
                <div
                    className="d-flex align-items-center gap-2"
                    style={{ cursor: row.idTenant ? "pointer" : "default" }}
                    onClick={() => row.idTenant && navigate(`/platform/tenants/${row.idTenant}`)}
                    title={row.idTenant ? "Ver detalhes do cliente" : undefined}
                >
                    <div
                        className="avatar-title text-white rounded-circle d-flex align-items-center justify-content-center fw-bold"
                        style={{ width: "32px", height: "32px", minWidth: "32px", background: "linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-accent) 100%)", fontSize: "11px", flexShrink: 0 }}
                    >
                        {(row.tenantName || row.schoolName || "?").substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                        <span className="fw-bold text-dark d-block text-truncate" style={{ maxWidth: "160px" }}>{row.tenantName || row.schoolName}</span>
                        <span className="text-xs text-muted d-block platform-mono">{row.id}</span>
                    </div>
                </div>
            ),
        },
        {
            key: "amount",
            label: "Valor / Vencimento",
            width: "20%",
            render: (row) => (
                <div>
                    <div className="fw-semibold">R$ {(row.amount || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
                    {row.dueDate && <span className="text-xs text-muted d-block">{new Date(row.dueDate).toLocaleDateString("pt-BR")}</span>}
                </div>
            ),
        },
        {
            key: "status",
            label: "Status / Liquidação",
            width: "20%",
            render: (row) => {
                const st = row.paymentStatus || row.status;
                return (
                    <div>
                        <NeoBadge color={BILLING_STATUS_COLORS[st] || "muted"}>
                            {BILLING_STATUS_LABELS[st] || st}
                        </NeoBadge>
                        {row.paidAt && <span className="text-xs text-muted d-block mt-1">{new Date(row.paidAt).toLocaleDateString("pt-BR")}</span>}
                    </div>
                );
            },
        },
        {
            key: "gateway",
            label: "Meio / Gateway",
            width: "20%",
            render: (row) => (
                <div>
                    <span className="fw-medium">{row.paymentMethodId || row.method || "—"}</span>
                    <span className="text-xs text-muted d-block">{row.gateway === "manual" ? "Baixa Manual" : "Gateway Online"}</span>
                </div>
            ),
        },
        {
            key: "actions",
            label: "Ações",
            width: "15%",
            align: "center",
            render: (row) => {
                const isPaid = row.paymentStatus === "approved" || row.status === "pago";
                return (
                    <div className="d-flex gap-1 justify-content-center">
                        {!isPaid && (
                            <NeoButton variant="success" size="sm" square onClick={() => handleOpenPaymentModal(row)} title="Registrar Pagamento" icon="mdi-check" />
                        )}
                        {isPaid && <div style={{ width: "30px" }} />}
                    </div>
                );
            },
        },
    ];

    const filterStatusComponent = (
        <div className="neo-filter-select-sm">
            <NeoInput type="select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} size="sm" className="mb-0">
                {STATUS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </NeoInput>
        </div>
    );

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Financeiro" />
            </div>

            <div className="platform-table-card">
                <NeoFinancialTable
                    columns={columns}
                    data={filteredSessions}
                    summaryData={summaryData}
                    summaryComponent={filterStatusComponent}
                    searchValue={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Buscar por academia ou código..."
                    loading={loading}
                />
            </div>

            <ConfirmPaymentModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                selectedFatura={selectedSession}
                formConfirm={formConfirm}
                onChangeField={(field, val) => setFormConfirm({ ...formConfirm, [field]: val })}
                onConfirm={handleConfirmPayment}
            />
        </div>
    );
};

export default PlatformBilling;
